import _ from "lodash";
import React from "react";
import ThemedModal from "../../utilities/ThemedModal";
import LoadingMessage from "../../loaders/LoadingMessage";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { ClientReportSendModalProps } from "./types";
import { SendClientReportOptionDefaults, SendMediaTypeOptions, SendMediaTypes } from "./constants";
import { ClientReportType } from "../../../types/ClientReportType";
import { DefReactSelectOption } from "../../../types/DefReactSelectOption";
import { confirmable, createConfirmation } from "../../../hooks/DialogMountPoint";
import { Modal } from "react-bootstrap";
import { Button } from "../..";
import { FormSelect } from "../../forms";
import { DashboardSendOptionsForm } from "./components/DashboardSendOptionsForm";
import { EmailSendOptionsForm } from "./components/EmailSendOptionsForm";
import { PostSendOptionsForm } from "./components/PostSendOptionsForm";
import { ClientPreferencesSummaryProvider, useClientPreferencesSummary } from "../../../hooks/ClientPreferencesSummaryContext";
import { SendClientReportOptions } from "../client-report-edit/hooks/ReportPublishContext";
import { useAuth } from "react-oidc-context";

export const ClientReportSendModal = ({
    backdrop = 'static',
    cancel,
    centered = true,
    defaultValues,
    dialogId,
    dismiss,
    proceed,
    report,
    show,
    size = "xl",
    ...rest
} : Readonly<ClientReportSendModalProps>) => {
    const [bodyAnimation] = useAutoAnimate();
    const formMethods = useForm<SendClientReportOptions>();
    const { control, handleSubmit, reset, setValue, watch } = formMethods;
    const { dashboardLogins, hasEmails, isAllCommunicationsStopped, isDashboardNotificationsEnabled, isEmailNotificationsEnabled, isLoading: isLoadingPreferences, preferences } = useClientPreferencesSummary();
    const selectedMediaType = watch('mediaType');

    // TODO: remove this
    // SH - this is a check to disable Email and Client Dashboard send options unless you are an admin
    // This is because we need to publish but the changes are merged
    const { user } = useAuth();
    const isAdminUser = _.some(user!.profile.roles, (item: string) => ["sys_admin_access"].includes(item));

    // build the default email addresses that will receive the notification that the report was sent
    const defaultEmailAddressesToNotify : string[] = React.useMemo(() => {
        if (!preferences
            || preferences.communication.emailNotificationRecipients.length <= 0
            || preferences.emails.length <= 0
        ) {
            return [];
        }
        const notificationRecipients = preferences.emails.filter(el => el.type === 2 && preferences.communication.emailNotificationRecipients.includes(el.id));
        return notificationRecipients.map((el) => el.details);
    }, [ preferences ]);

    // build a custom set of options based on the current clients preferences
    // here we can disable options based on the client preferences, like disabling access to sending via email
    const mediaTypeSelectOptions : DefReactSelectOption<void>[] = React.useMemo(() => {
        return SendMediaTypeOptions.map((el) => {
            if (el.value === SendMediaTypes.EMAIL) {
                return { ...el, isDisabled: isAllCommunicationsStopped || !isEmailNotificationsEnabled || !hasEmails || !isAdminUser };
            }
            if (el.value === SendMediaTypes.CLIENTDASHBOARD) {
                return { ...el, isDisabled: dashboardLogins.length === 0 || isAllCommunicationsStopped || !isDashboardNotificationsEnabled || !isAdminUser };
            }
            return { ...el } as DefReactSelectOption<void>;
        })
    }, [ SendMediaTypeOptions, dashboardLogins, isDashboardNotificationsEnabled, isEmailNotificationsEnabled, user ]);

    const onClose = () => cancel!();

    const onResetForm = (mediaType: number) => {
        reset({ ...SendClientReportOptionDefaults, mediaType, emailAddressesToNotify: defaultEmailAddressesToNotify });
    };

    const onSubmit = async (form: SendClientReportOptions) => {
        // if we are going to send via email, then we want to alert them that it tries to create the draft ready, before sending
        // this is just to breakdown the responsibilities of the overall feature. Passing up all that data in the patch, could be a bit
        // awkward getting it over to graph via rabbit without losing data/potentially causing issue. Randomly creating drafts seems like a safe option
        if (form.mailMessage && selectedMediaType === SendMediaTypes.EMAIL || form.isNotifiedViaEmail === true) {
            // TODO: we want to alert the user that the user is going to notify the client via email and that it will create and send a draft from X email.
            proceed!(form);
        }
        else {
            // handles all other instances where it doesn't require an email to be drafted via graph
            // this would be for via online dashboard or via post (standard/old method)
            proceed!(form);
        }
    };

    // whenever the default values change/we basically open the modal here.
    React.useEffect(() => {
        if (!defaultValues) {
            return;
        }
        reset({ ...defaultValues });
    }, [ defaultValues ]);

    // whenever we have finished loading the preferences
    React.useEffect(() => {
        if (defaultValues && defaultValues.emailAddressesToNotify.length > 0) {
            return;
        }
        setValue('emailAddressesToNotify', defaultEmailAddressesToNotify);
    }, [ defaultEmailAddressesToNotify ]);

    return (
        <Modal backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header variant="primary">
                <Modal.Title>Set Report Publish Options</Modal.Title>
            </ThemedModal.Header>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Body ref={bodyAnimation}>
                        {isLoadingPreferences && (
                            <LoadingMessage text="Please wait while WealthPro checks the Client Preferences" />
                        )}
                        {!isLoadingPreferences && (
                            <>
                                <div className="row gy-2 mb-2">
                                    <div className="col-12">
                                        <Controller
                                            name="mediaType"
                                            control={control}
                                            render={({ field: { ref, value, onChange, ...rest } }) => (
                                                <FormSelect
                                                    label="Media Type"
                                                    defaultValue={value}
                                                    disableAnimations={true}
                                                    options={mediaTypeSelectOptions}
                                                    onChange={(rec : DefReactSelectOption<void>) => onResetForm(rec.value)}
                                                    {...rest}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                {selectedMediaType === SendMediaTypes.EMAIL && (
                                    <EmailSendOptionsForm 
                                        report={report} 
                                        onEmailFormChanged={(v: MailMessage) => setValue('mailMessage', v, { shouldDirty: true })}
                                    />
                                )}
                                {selectedMediaType === SendMediaTypes.POST && (
                                    <PostSendOptionsForm report={report} />
                                )}
                                {selectedMediaType === SendMediaTypes.CLIENTDASHBOARD && (
                                    <DashboardSendOptionsForm report={report} />
                                )}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="success" type="submit" disabled={isLoadingPreferences}>Confirm</Button>
                        <Button variant="light" onClick={onClose}>Close</Button>
                    </Modal.Footer>
                </form>
            </FormProvider>
        </Modal>
    );
};

export function openSendClientReportModal(report: ClientReportType, defaultValues: SendClientReportOptions | null = null) {
    return createConfirmation<ClientReportSendModalProps, SendClientReportOptions>(confirmable((props) => (
        <ClientPreferencesSummaryProvider masterAccountId={report.masterAccountId}>
            <ClientReportSendModal {...props} />
        </ClientPreferencesSummaryProvider>
    )))({ 
        dialogId: 'client_report_send_modal',
        defaultValues: defaultValues ?? SendClientReportOptionDefaults,
        report
    });
};